// QoE local: TTFF, zapping, FPS, rebuffer, errors.
// Buffer em memória + flush periódico pro backend.
//
// REGRA DO DONO: "telemetria deveria ser fire-and-forget, não pode impactar a
// performance". Este é o ponto de telemetria PURA do app — nada aqui é
// funcional, nada aqui é esperado por ninguém. Logo:
//
//  - Sai por `runFireAndForget` (net-priority): durante a janela crítica
//    (sign → buffering → 1º frame) NENHUM byte de QoE vai pra rede. Sem teto:
//    se o usuário está esperando um frame, a QoE não existe.
//  - Vai pelo `telemetry-transport` (nunca pelo `request()` do api.ts): 1
//    tentativa, sem failover, sem rotação de endpoint, sem refresh que desloga.
//    Ver o cabeçalho do telemetry-transport.ts pro porquê de cada um.
//  - Serializa fora do frame (o transporte cede a main thread antes do
//    JSON.stringify) — o buffer chega a 200 eventos.
//  - Falha é engolida: rede caída = telemetria some, app segue.
//  - Fim de sessão sai por `visibilitychange → hidden` + `pagehide` (nunca
//    `beforeunload`, que não é confiável em mobile/bfcache), num envio só, via
//    `postTelemetryUnload` (fetch com keepalive quando o browser tem).
//    Ver sendEndOfSession.
//
// NÃO importa `api.ts` de propósito: isso também quebra o ciclo de import
// api → qoe → api que existia (api.ts importa reportCacheHit/Miss daqui).

import { detectPlatform } from '@app-iptv/device'

import { abandonCriticalPath, runFireAndForget } from './net-priority'
import {
  postTelemetry,
  postTelemetryUnload,
  SERVER_BODY_BUDGET_BYTES,
  sizeOf,
} from './telemetry-transport'

/** Metadados extras opcionais — backend aceita payload arbitrário em
 *  `payload: jsonb`. Eventos podem incluir capabilities-derived telemetry
 *  (cap_*), heap, idb usage, etc. Tipo aberto pra extensibilidade sem
 *  forçar todos os tipos a listar campos — caller declara via intersection. */
type QoeMeta = Record<string, string | number | boolean>

type QoeEvent =
  | { type: 'ttff'; ms: number; channelId: string; title?: string | undefined; ts: number }
  | { type: 'zapping'; ms: number; from: string; to: string; ts: number }
  | { type: 'rebuffer'; ms: number; channelId: string; ts: number }
  | { type: 'player_error'; code: string; channelId: string; ts: number }
  // auto_switch — emitido em cada tentativa de troca de fonte por causa
  // de falha (player error, stalled, freeze, blocked). Permite identificar
  // canais "ruins" (taxa de switch alta) e validar se a feature está
  // funcionando em produção.
  // - reason: tipo da falha que disparou
  // - groupKey: chave canônica do canal (ex: "globo-sp")
  // - fromChannelId/toChannelId: fonte antiga → fonte nova (source-level)
  // - attemptIdx: número da tentativa nessa cadeia (1, 2, 3...)
  | {
      type: 'auto_switch'
      reason: 'initial-error' | 'runtime-error' | 'stalled' | 'blocked' | 'sign'
      groupKey: string
      fromChannelId: string
      toChannelId: string
      attemptIdx: number
      ts: number
    }
  // auto_switch_exhausted — emitido quando todas as sources do grupo foram
  // tentadas e nenhuma funcionou. Dashboard: canais com taxa alta = provider
  // degradado ou todos os streams indisponíveis. Diferente de player_error
  // (que pode ser 1 falha pontual) — aqui significa "fim da linha".
  | {
      type: 'auto_switch_exhausted'
      groupKey: string
      lastChannelId: string
      attempts: number
      ts: number
    }
  // endpoint_failover (F3) — emitido quando, ESGOTADAS as fontes do canal no
  // endpoint atual, o app pede ao backend o PRÓXIMO endereço do provider
  // (re-sign com avoidEndpointId). Permite cruzar no Grafana com os
  // endpoint_failover_events do backend (reason='client_failover'): se a taxa
  // sobe, um endereço do provider está degradado e o failover está salvando o
  // play. `attemptIdx` é a rodada de endereço nesta cadeia (1..MAX).
  | {
      type: 'endpoint_failover'
      reason: 'initial-error' | 'runtime-error' | 'stalled' | 'blocked' | 'sign'
      groupKey: string
      channelId: string
      avoidEndpointId: string
      attemptIdx: number
      ts: number
    }
  // provider_unavailable (fix/tv-provider-unavailable-no-storm) — o sign do ao
  // vivo recebeu 503 provider_unavailable: o PROVEDOR está fora/rate-limitado,
  // não é fonte ruim. phase 'backoff' = esperando retryAfter pra re-tentar a
  // MESMA fonte (NÃO rotaciona — rotacionar floda o edge do provedor);
  // 'giveup' = estourou o teto de re-tentativas → mensagem calma "tente mais
  // tarde". Cruza no Grafana com o circuit breaker do backend pra PROVAR que a
  // tempestade de sign parou (de ~15 POSTs/tap pra ~1 + poucas espaçadas).
  | {
      type: 'provider_unavailable'
      phase: 'backoff' | 'giveup'
      channelId: string
      retryAfterSec: number
      attempt: number
      ts: number
    }
  // attempted_reset — emitido quando attemptedSourcesRef é zerado (Bug 4
  // 2026-05-14). Sources possíveis:
  // - 'visibility' — voltou do background após >30s em hidden
  // - 'online' — voltou online após >60s offline (standby longo)
  // - 'stabilize' — 30s tocando estável (recuperação real)
  // - 'retry' — user clicou "Tentar de novo" no overlay de erro
  // Permite Fernando consultar QoE pós-fix e validar que rotação está
  // tendo chance de esgotar fontes em vez de travar prematuramente.
  | {
      type: 'attempted_reset'
      source: 'visibility' | 'online' | 'stabilize' | 'retry'
      ts: number
    }
  | ({ type: 'fps_drop'; fps: number; tier: string; ts: number } & QoeMeta)
  // health_sample — telemetria unificada emitida pelo health-watcher
  // (consolida fps-watchdog + long-session-cleaner). Cobre FPS, heap
  // pressure, IDB usage, long-session mode num único evento — Grafana
  // cruza tudo sem precisar JOIN entre séries separadas.
  // Substitui os antigos fps_avg + fps_avg-portador-de-cleanup-stats.
  | ({
      type: 'health_sample'
      ts: number
      tier: string
      fps: number
      long_session_mode: 'normal' | 'warn' | 'degrade' | 'suggest'
    } & QoeMeta)
  // cleanup_sample — emitido 1×/h pelo runCleanup E pelo
  // idb-quota-manager (quando decide agir). Carrega quanto foi GC'd
  // (vod, series, RQ inativas) + heap pós-cleanup. Do quota-manager
  // vem com fields extras: quota_action ('none'|'proactive'|'aggressive'),
  // quota_pressure_before/after (% da quota efetiva).
  | ({
      type: 'cleanup_sample'
      ts: number
      tier: string
      cleanup_vod_pruned: number
      cleanup_series_pruned: number
      cleanup_rq_removed: number
      cleanup_elapsed_ms: number
    } & QoeMeta)
  // long_session_transition — emitido quando longSessionMode muda
  // (normal→warn→degrade→suggest). Útil pra alertar no Grafana
  // ("usuário X virou suggest").
  | {
      type: 'long_session_transition'
      ts: number
      tier: string
      from: 'normal' | 'warn' | 'degrade' | 'suggest'
      to: 'normal' | 'warn' | 'degrade' | 'suggest'
    }
  | { type: 'session_start'; ts: number; tier: string; platform: string }
  | { type: 'route_change'; path: string; ts: number }
  // Poster missing (Camada 5 do poster-resolver-centralizado): emitido
  // SOMENTE quando VodCard renderiza placeholder de letra — backend
  // confirmou que NEM TMDB NEM provider têm cover. Volume esperado
  // <0.5% dos cards renderizados (raro). Permite agregação retrospectiva
  // por kind/sourceId pra investigar items recorrentes sem poster.
  | {
      type: 'poster_missing'
      kind: 'vod' | 'series'
      sourceId?: string
      ts: number
    }
  // Snapshot/cache local (Fase 3.7) — agregado em janelas e enviado periodicamente
  | {
      type: 'catalog_cache_stats'
      ts: number
      hits: number
      misses: number
      latencyMsAvg: number // tempo médio de resposta (cache + miss)
    }
  // Refresh client-side (#8) — revalidação do catálogo disparada por
  // polling 30min ou visibilitychange. `source` discrimina o trigger;
  // `result` mede efetividade do ETag (304 = cache backend válido,
  // 200 = catálogo mudou, error = falha rede/backend).
  // 'cache' é defensivo (nunca emitido pelo hook que sempre passa
  // force:true ao loadSnapshot — TTL local 24h é furado).
  //
  // `source: 'boot'` (boot-snapshot.ts) é o fetch de BOOT, o único que NÃO
  // passa force:true — ele só baixa quando o snapshot local está ausente/stale.
  // Era invisível na telemetria (só o `snapshot_write` do catalog-store
  // aparecia), o que impedia fechar a conta de "o download de boot roda em TODO
  // launch?" olhando o device: 'boot' presente a cada launch + `snapshot_write`
  // com quotaExceeded/idbUnavailable = gravação falhando, não catálogo mudando.
  | {
      type: 'catalog_revalidated'
      ts: number
      source: 'poll' | 'visibility' | 'session-start' | 'boot'
      result: '304' | '200' | 'cache' | 'error'
      providerScope: string
      elapsedMs: number
    }
  // Auth invalidado pelo backend (401/403) ou por client-side check
  // (accountExpiresAt). Permite Grafana medir frequência e cortar
  // distribuir alertas (pico de session_expired = backend instável;
  // pico de account_expired = problema de pagamento massivo).
  | {
      type: 'auth_invalidated'
      ts: number
      reason: 'session_expired' | 'account_expired'
      source: 'backend' | 'client_expiry_check'
    }
  // Banda re-medida em background (hook useBandwidthRefresh, 4h).
  // `mbpsBefore`/`classBefore` permitem detectar saltos (Wi-Fi pior à
  // noite). `applied=false` quando hysteresis bloqueou (mudança não
  // confirmada por 2 medições consecutivas).
  | {
      type: 'bandwidth_remeasured'
      ts: number
      mbpsBefore: number | null
      mbpsAfter: number
      classBefore: 'poor' | 'low' | 'medium' | 'high' | 'ultra' | null
      classAfter: 'poor' | 'low' | 'medium' | 'high' | 'ultra'
      applied: boolean
      source: 'cloudflare' | 'self' | 'none'
    }
  // snapshot_write — RASTRO da gravação do snapshot no IndexedDB. Existe porque
  // a falha de persistência era INVISÍVEL: o `set()` engolia QuotaExceededError
  // num catch vazio e a UI seguia dizendo "N títulos atualizados" com o IDB
  // VAZIO (medido na LG do dono: 3 boots, 0 records, 5 leitoras sempre na rede).
  // `result` distingue os 3 mundos que antes colapsavam em "silêncio":
  //   - 'ok'        = tudo gravado
  //   - 'partial'   = parte gravada (ex: liveChannels entrou, vodItems não coube)
  //   - 'failed'    = nada gravado (cota estourou já na 1ª surface)
  //   - 'unavailable' = IDB inteiro fora (Chr38 sem Proxy, modo privado) — NÃO é
  //     bug: é o fallback de rede assumindo, e precisa ser distinguível de cota.
  // `surfacesPersisted`/`bytesEstimate` deixam o Grafana achar o teto real de
  // cota por tier sem depender de navigator.storage.estimate (ausente no piso).
  | {
      type: 'snapshot_write'
      ts: number
      result: 'ok' | 'partial' | 'failed' | 'unavailable'
      quotaExceeded: boolean
      surfacesPersisted: number
      surfacesTotal: number
      /** Surfaces que não entraram — o nome importa: perder `vodItems` degrada
       *  Filmes pra rede; perder `liveChannels` mata o caminho crítico do hero. */
      failedSurfaces: string
      itemsTotal: number
      elapsedMs: number
      tier: string
      /** Por que o disjuntor deixou gravar: 'ausente' (não havia a fatia em
       *  disco), 'mudou' (o conteúdo mudou), 'intervalo' (mudou e o intervalo
       *  mínimo da prioridade já passou), 'manual' ("Atualizar catálogo"). */
      motivo?: string
      /** Modo do disjuntor na hora: 'full' | 'priority-only'. */
      modo?: string
      /** Gravações REAIS (chamadas de `set()`) nesta sessão, contando esta. É a
       *  conta que mede se a sangria de capacidade parou no aparelho. */
      gravacoesSessao?: number
    }
  // snapshot_write_skipped — o disjuntor DECIDIU NÃO GRAVAR. Separado do
  // `snapshot_write` de propósito: quem já conta aquele evento como "gravação
  // real" continua contando certo. `motivo`:
  //   - 'breaker-off' = aparelho não comporta nem a prioridade
  //   - 'unchanged'   = o disco já tem este conteúdo
  //   - 'throttled'   = a prioridade mudou (ou não está inteira em disco), mas a
  //                     última gravação OU tentativa dela foi há menos que o
  //                     intervalo mínimo
  | {
      type: 'snapshot_write_skipped'
      ts: number
      motivo: 'breaker-off' | 'unchanged' | 'throttled'
      modo: string
      /** Pulos nesta sessão, contando este. */
      pulosSessao: number
      tier: string
    }
  // catalog_db_reset — a AUTO-CURA da cota apagou o `iptv-catalog` (disjuntor em
  // `off`; ver catalog-auto-cura.ts). Uma vez por episódio de `off`. Só enums,
  // nada que identifique conteúdo ou aparelho. `motivo`: 'boot' (o aparelho já
  // estava em `off` ao abrir) ou 'transicao' (caiu pra `off` nesta sessão).
  // `resultado`: 'ok', 'timeout' (delete bloqueado além do prazo) ou 'erro'.
  | {
      type: 'catalog_db_reset'
      ts: number
      motivo: 'boot' | 'transicao'
      resultado: 'ok' | 'timeout' | 'erro'
      tier: string
    }
  // telemetry_truncated — RASTRO de truncagem do envio de fim de sessão. O
  // keepalive tem quota de 64KB e estourar não dá erro: o request some. Então o
  // batch é cortado (ver telemetry-transport.fitKeepaliveBatch) e ESTE evento
  // viaja junto, contando quanto ficou pra trás. Sem ele, a perda seria
  // silenciosa: um batch de 190 eventos truncado é idêntico, no Grafana, a uma
  // sessão que só gerou 190. Volume esperado ~0 (só sessão muito longa sem
  // flush) — se aparecer com frequência, o MAX_BUFFER está grande demais.
  | { type: 'telemetry_truncated'; ts: number; dropped: number; kept: number }
  // telemetry_rejected — RASTRO de lote DESCARTADO por recusa definitiva do backend
  // (400/413/422) ou por evento individualmente grande demais pro orçamento do flush.
  // Irmão do `telemetry_truncated` e existe pelo mesmo motivo: sem ele a perda é
  // indistinguível de "a sessão gerou menos eventos". A diferença entre os dois é ONDE
  // a decisão foi tomada — `truncated` é o CLIENTE cortando pra caber na quota do
  // keepalive; `rejected` é o SERVIDOR recusando, ou um evento que não cabe em lote
  // nenhum. Volume esperado ZERO: se aparecer, ou um cliente engordou um tipo de evento
  // além do teto de bytes da rota, ou o teto do servidor foi apertado demais.
  | { type: 'telemetry_rejected'; ts: number; dropped: number }
  // sync_group_rejected — o flush pulou um grupo do outbox que o servidor recusou
  // (403 `profile_not_owned`: dado de OUTRA conta na TV). As entradas ficam no outbox
  // e o perfil não é tentado de novo na sessão. Ou (`reason: 'ownerless_quarantine'`,
  // AP-116) entradas SEM dono, de antes do dono existir, cujo perfil não é da conta da
  // sessão: ficam em quarentena até a próxima limpeza do outbox. Contagem, nunca ids.
  | { type: 'sync_group_rejected'; ts: number; reason: string; entries: number }
  // sync_item_quarantined — o flush tirou itens da fila de envio porque o servidor os
  // recusaria pra sempre (`status: null` = a validação local já reprovou, sem pedido) ou
  // recusou o item isolado com 400/413/422 (sync/outbox.ts, "QUARENTENA"). As entradas
  // ficam guardadas no outbox pra diagnóstico. Agregado por flush. Contagem, nunca ids.
  | { type: 'sync_item_quarantined'; ts: number; reason: string; status: number | null; entries: number }
  // sync_context_missing — há sessão Swipe mas não há CONTEXTO de sync (provider +
  // usuário da fonte) neste aparelho: nada sobe nem desce. `reason`: `no_account`
  // (o puller achou o store sem fonte), `no_source` (a conta não tem fonte) ou
  // `resolve_failed` (a fonte não pôde ser lida). `surface`: tv | mobile. Uma vez por
  // sessão e motivo. Sem id.
  | { type: 'sync_context_missing'; ts: number; reason: string; surface: string }
  // stamp_migration — uma rodada da limpeza dos carimbos do auto-advance (uma vez por
  // aparelho, com reenvio dos tombstones pendentes a cada boot). Contagens, nunca ids.
  // `tombstones` = desfecho de cada pendente nesta rodada (queued/superseded/...).
  | {
      type: 'stamp_migration'
      ts: number
      scanComplete: boolean
      /** O relógio do boot estava antes do piso (TV sem RTC antes do NTP): a limpeza
       *  local não rodou. */
      clockBehind: boolean
      groups: number
      pendingTies: number
      /** A limpeza local fechou pelo teto global (conservador). */
      gaveUp: boolean
      removedPositions: number
      removedSeriesProgress: number
      rewrittenSeriesProgress: number
      /** Snapshots mais novos que as posições que sobraram, recalculados só local. */
      localOnlySeriesProgress: number
      tombstones: Record<string, number>
    }
  // stamp_migration_push — o que o SERVIDOR fez com um lote de push que levava
  // tombstones da migração. `applied`/`ignored` são do lote inteiro (o servidor
  // devolve só as contagens); `tombstones` diz quantos do lote eram da migração.
  // `ignored > 0` NÃO é falha: tombstone ancorado no carimbo PERDE pra escrita
  // legítima posterior (ex.: o T2E7 do GoT, re-assistido em 04/09).
  | {
      type: 'stamp_migration_push'
      ts: number
      applied: number
      ignored: number
      tombstones: number
      batch: number
    }

const buffer: QoeEvent[] = []
const FLUSH_INTERVAL_MS = 30_000
const MAX_BUFFER = 200
// Backoff em rede ruim crônica: após falha, aguarda esse intervalo
// antes de tentar novo flush. Sem isso, o buffer enche, dispara flush,
// falha, re-enfileira, dispara de novo — hot loop. M3 do code review.
const FLUSH_BACKOFF_MS = 60_000
/**
 * Orçamento de BYTES do flush periódico — o MESMO teto do servidor, importado de
 * `telemetry-transport` pra não existir um segundo número (ver SERVER_BODY_BUDGET_BYTES).
 *
 * Até aqui só o envio de FIM DE SESSÃO tinha orçamento (a quota do keepalive); o flush
 * periódico mandava o buffer inteiro sem olhar tamanho — então era ele, e não o unload,
 * que batia no teto do servidor.
 *
 * ⚠️ O QUE ELE FAZ NÃO É DESCARTAR, é FATIAR: manda o que cabe e DEIXA o resto no buffer
 * pro próximo flush. Diferente do fim de sessão, aqui existe um próximo flush — não há
 * motivo pra perder evento. Só é descartado o evento que sozinho não cabe em lote nenhum
 * (ver `flush`), porque esse jamais seria enviável e travaria todos atrás dele.
 */
const FLUSH_BUDGET_BYTES = SERVER_BODY_BUDGET_BYTES

let flushTimer: ReturnType<typeof setInterval> | null = null
let started = false
let lastFlushFailedAt = 0
let flushing = false
let onVisibility: (() => void) | null = null
let onPageHide: (() => void) | null = null
// Epoch da sessão de telemetria: incrementa a cada stopQoe(). Um batch em voo
// que falha só pode voltar pro buffer se a sessão AINDA for a mesma — senão um
// stopQoe→startQoe (re-login) no meio do envio re-enfileiraria eventos do
// usuário ANTERIOR, que sairiam depois com o Bearer do NOVO. É a mesma
// preocupação que o `buffer.length = 0` do stopQoe já tem, estendida ao que está
// no ar quando ele roda.
let sessionEpoch = 0

function nowSec(): number {
  return Math.floor(Date.now() / 1000)
}

/**
 * Devolve pro buffer um batch que o backend não aceitou, mantendo os MAIS
 * RECENTES (fim do batch) — velhos são descartados se o buffer encheu nesse meio
 * tempo. No-op se a sessão virou durante o envio (ver sessionEpoch).
 */
function requeueFailedBatch(batch: QoeEvent[], epoch: number): void {
  if (epoch !== sessionEpoch) return
  lastFlushFailedAt = Date.now()
  const room = MAX_BUFFER - buffer.length
  if (room > 0) buffer.unshift(...batch.slice(-room))
}

export function track(ev: QoeEvent): void {
  buffer.push(ev)
  // Drop oldest se exceder cap. Eventos recentes valem mais que velhos
  // pra QoE — perder amostra de 30min atrás é melhor que perder a atual.
  if (buffer.length > MAX_BUFFER) buffer.shift()
  // Em dev, log pra inspeção (debug — não aparece no console por padrão,
  // ative "Verbose" no DevTools pra ver)
  // eslint-disable-next-line no-console
  if (import.meta.env.DEV) console.debug('[qoe]', ev)
}

/**
 * Envia o buffer acumulado num ÚNICO request (coalescing: N eventos → 1 POST).
 *
 * Nunca lança. Nunca re-tenta na hora (o backoff abaixo é o que evita o hot
 * loop flush→falha→re-enqueue→flush em rede ruim crônica).
 */
/**
 * Tira do buffer o maior PREFIXO que cabe no orçamento de bytes, deixando o resto pro
 * próximo flush. Devolve também os eventos INDIVIDUALMENTE grandes demais, que são
 * descartados: um evento que sozinho estoura o orçamento não cabe em lote nenhum, e
 * mantê-lo no buffer bloquearia todos os que vêm depois dele para sempre.
 */
function takeWithinBudget(): { batch: QoeEvent[]; oversized: number } {
  const batch: QoeEvent[] = []
  let bytes = 0
  let oversized = 0
  while (buffer.length > 0) {
    const ev = buffer[0] as QoeEvent
    let tamanho: number
    try {
      // BYTES UTF-8, não code units: `JSON.stringify(x).length` conta UTF-16 e um
      // título acentuado (`ó` = 2 bytes) faria este orçamento render o dobro. Mesmo
      // medidor que o orçamento do keepalive usa — ver `sizeOf`.
      tamanho = sizeOf(ev) + 1 // +1 pela vírgula do array
    } catch {
      // Evento não serializável (ciclo): descarta aqui em vez de derrubar o lote
      // inteiro lá no transporte.
      buffer.shift()
      oversized++
      continue
    }
    if (tamanho > FLUSH_BUDGET_BYTES) {
      // Sozinho já não cabe: nunca será enviável. Descarta e segue.
      buffer.shift()
      oversized++
      continue
    }
    if (bytes + tamanho > FLUSH_BUDGET_BYTES) break // enche o próximo lote
    bytes += tamanho
    batch.push(ev)
    buffer.shift()
  }
  return { batch, oversized }
}

/**
 * O backend RECUSOU este corpo em definitivo (400/413/422). NÃO devolve ao buffer —
 * devolver é o que criava o loop silencioso: mesmo lote, mesma recusa, a cada backoff,
 * para sempre. Deixa rastro no lugar, pra que a perda apareça no servidor em vez de
 * sumir: um `telemetry_rejected` (pequeno) entra no buffer e sobe no próximo flush.
 */
function dropRejectedBatch(batch: QoeEvent[], epoch: number, retryable: boolean): void {
  if (epoch !== sessionEpoch) return
  lastFlushFailedAt = Date.now()
  if (retryable) return
  track({ type: 'telemetry_rejected', ts: nowSec(), dropped: batch.length })
}

export async function flush(): Promise<void> {
  if (buffer.length === 0) return
  // Backoff: se último flush falhou há <FLUSH_BACKOFF_MS, pula. Evita
  // hot loop de flush→falha→re-enqueue→flush em rede ruim crônica.
  if (Date.now() - lastFlushFailedAt < FLUSH_BACKOFF_MS) return
  // Re-entry guard: múltiplos `track()` no mesmo tick poderiam
  // disparar flushes paralelos (cada um veria buffer cheio antes do
  // primeiro splice). N-1 enviariam batch vazio. Flag impede.
  if (flushing) return
  flushing = true
  const epoch = sessionEpoch
  try {
    // A janela crítica é esperada ANTES de tirar os eventos do buffer: se o
    // usuário der play/zap agora, os eventos continuam no buffer (e o próximo
    // track() ainda os coalesce) em vez de ficarem pendurados num batch em
    // voo que ninguém vai reenfileirar se o app morrer nesse meio tempo.
    await runFireAndForget(async () => {
      // FATIA por bytes em vez de drenar tudo: o resto fica pro próximo flush. Sem isto
      // um buffer gordo estourava o teto do servidor e voltava 413 — e o requeue
      // reenviava o mesmo lote indefinidamente.
      const { batch, oversized } = takeWithinBudget()
      // O rastro viaja NO PRÓPRIO lote, não via `track()`: `track()` empurraria pro
      // buffer, e o marcador só chegaria no flush seguinte — ou nunca, se a sessão
      // terminasse antes. Ele é pequeno (~60 B) e cabe na margem entre o orçamento
      // (120 KiB) e o teto do servidor (128 KiB) — 8.122 B de sobra medidos no pior caso,
      // com o marcador empurrado DEPOIS do corte justamente pra que ele não cause o 413
      // que existe pra reportar.
      if (oversized > 0) batch.push({ type: 'telemetry_rejected', ts: nowSec(), dropped: oversized })
      if (batch.length === 0) return
      // postTelemetry NÃO lança e serializa fora do frame.
      const r = await postTelemetry('/v1/qoe', { events: batch })
      if (!r.ok) {
        // A distinção é o conserto: só volta pro buffer o que TEM chance de ser aceito.
        if (r.retryable) requeueFailedBatch(batch, epoch)
        else dropRejectedBatch(batch, epoch, false)
      }
    })
  } finally {
    flushing = false
  }
}

/**
 * Rastro de truncagem do fim de sessão — o `makeMarker` que o transporte injeta
 * no batch quando corta. Ver o tipo `telemetry_truncated` lá em cima.
 */
function truncationMarker(dropped: number, kept: number): QoeEvent {
  return { type: 'telemetry_truncated', ts: nowSec(), dropped, kept }
}

/**
 * FIM DE SESSÃO: um desenho, dois gatilhos.
 *
 * - `visibilitychange → hidden` é o que vale na TV: o app não "fecha aba", vai
 *   pra STANDBY e pode ser morto depois. Aqui ele ainda está VIVO e o fetch
 *   normal entrega.
 * - `pagehide` é o que vale no browser/mobile, inclusive quando a página vai pro
 *   bfcache (congelada, não descarregada).
 *
 * `beforeunload` saiu da jogada: não dispara de forma confiável em mobile nem no
 * caminho do bfcache — era a "rede de segurança" que mais furava.
 *
 * SEM ENVIO DUPLICADO, e por CONSTRUÇÃO, não por flag: num unload de browser os
 * dois gatilhos disparam (hidden primeiro, pagehide logo atrás). O primeiro
 * DRENA o buffer sincronamente, então o segundo encontra `buffer.length === 0` e
 * vira no-op. O mesmo `splice` síncrono resolve a corrida com um flush represado
 * que o `abandonCriticalPath()` abaixo acabou de liberar: quando o callback dele
 * rodar (microtask), o buffer já está vazio e ele não manda nada.
 */
function sendEndOfSession(): void {
  if (buffer.length === 0) return
  // Mesmo backoff do flush: rede ruim crônica não pode virar hot loop de envio a
  // cada hidden/visible (a TV alterna standby, o browser alterna aba).
  if (Date.now() - lastFlushFailedAt < FLUSH_BACKOFF_MS) return
  // O caminho crítico acabou: ninguém está esperando um frame de um app que foi
  // pra standby. Sem isto, um flush já represado na janela (que não tem teto)
  // seguraria o fim de sessão que este handler existe pra entregar.
  abandonCriticalPath()
  const batch = buffer.splice(0, buffer.length)
  const epoch = sessionEpoch
  // Sem esperar: se o app for morto no meio, perdemos o batch — que é
  // exatamente o contrato de fire-and-forget.
  void postTelemetryUnload('/v1/qoe', batch, truncationMarker).then((ok) => {
    // `hidden` na TV não é o fim do mundo: o app volta do standby e o próximo
    // flush tenta de novo. No unload de verdade isto simplesmente não roda.
    if (!ok) requeueFailedBatch(batch, epoch)
  })
}

export function startQoe(tier: string): void {
  if (started) return
  started = true

  track({
    type: 'session_start',
    ts: nowSec(),
    tier,
    platform: detectPlatform(),
  })

  flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS)

  // ===== Fim de sessão: os dois gatilhos (ver sendEndOfSession) =====
  onVisibility = (): void => {
    // visibilitychange dispara nas DUAS direções; só `hidden` é fim de sessão.
    if (document.visibilityState !== 'hidden') return
    sendEndOfSession()
  }
  document.addEventListener('visibilitychange', onVisibility)

  onPageHide = (): void => {
    sendEndOfSession()
  }
  window.addEventListener('pagehide', onPageHide)
}

export function stopQoe(): void {
  if (flushTimer) clearInterval(flushTimer)
  flushTimer = null
  if (cacheFlushTimer) clearInterval(cacheFlushTimer)
  cacheFlushTimer = null
  // Zera o estado do buffer: um stopQoe→startQoe (re-login) NÃO pode mandar
  // eventos do usuário ANTERIOR com o Bearer do novo. E `flushing` precisa
  // voltar a false, senão um flush represado no momento do stop deixaria o
  // guard travado pra sempre e nenhum flush futuro sairia.
  buffer.length = 0
  flushing = false
  lastFlushFailedAt = 0
  // Invalida o que está EM VOO: um batch do usuário anterior que falhe depois
  // deste ponto não pode voltar pro buffer (ver sessionEpoch/requeueFailedBatch).
  // Sem isto, esvaziar o buffer acima não bastaria.
  sessionEpoch++
  // Sem remover os listeners, um startQoe/stopQoe repetido (long session,
  // re-login) empilharia handlers — leak de listener é exatamente o que o
  // long-session-cleaner existe pra evitar.
  if (onVisibility) {
    document.removeEventListener('visibilitychange', onVisibility)
    onVisibility = null
  }
  if (onPageHide) {
    window.removeEventListener('pagehide', onPageHide)
    onPageHide = null
  }
  started = false
}

// ===== Cache stats agregadas (Fase 3.7) =====
// Wrappers de api.* (local-first) chamam reportCacheHit/Miss. Agregamos em
// janela e enviamos como 1 evento periódico (não 1 por request — seria spam).

let cacheHits = 0
let cacheMisses = 0
let cacheLatencyTotal = 0
let cacheLatencyCount = 0
const CACHE_FLUSH_INTERVAL_MS = 60_000
let cacheFlushTimer: ReturnType<typeof setInterval> | null = null

export function reportCacheHit(latencyMs: number): void {
  cacheHits++
  cacheLatencyTotal += latencyMs
  cacheLatencyCount++
  ensureCacheFlushTimer()
}

export function reportCacheMiss(latencyMs: number): void {
  cacheMisses++
  cacheLatencyTotal += latencyMs
  cacheLatencyCount++
  ensureCacheFlushTimer()
}

function ensureCacheFlushTimer(): void {
  if (cacheFlushTimer) return
  cacheFlushTimer = setInterval(() => {
    if (cacheHits === 0 && cacheMisses === 0) return
    track({
      type: 'catalog_cache_stats',
      ts: nowSec(),
      hits: cacheHits,
      misses: cacheMisses,
      latencyMsAvg: cacheLatencyCount > 0 ? Math.round(cacheLatencyTotal / cacheLatencyCount) : 0,
    })
    cacheHits = 0
    cacheMisses = 0
    cacheLatencyTotal = 0
    cacheLatencyCount = 0
  }, CACHE_FLUSH_INTERVAL_MS)
}
